/**
 * ReasoningPanel — Collapsible LLM reasoning / tools / raw-token inspector.
 *
 * Default: collapsed header with step/tool/token counts.
 * Expanded sections (independent toggles):
 *   - Steps (routing / status)
 *   - Thinking (assembled delta stream)
 *   - Raw tokens (verbatim delta chunks)
 *   - Tools
 *
 * Props:
 *   reasoningSteps  {Array}
 *   liveText        {string}
 *   rawTokens       {Array<string>}
 *   thinkingText    {string}
 *   toolActions     {Array}
 *   isStreaming      {boolean}
 *   compact         {boolean}
 *   defaultOpen     {boolean}  Outer panel (default: false — keep chat clean)
 *   title           {string}
 */
import React, { useState, useEffect, useRef } from 'react';
import './ReasoningPanel.css';

function SubSection({ label, count, children, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    if (!children && !count) return null;
    return (
        <div className="rp-subsection">
            <button
                type="button"
                className="rp-subhead"
                onClick={() => setOpen((v) => !v)}
            >
                <span className="rp-toggle">{open ? '▾' : '▸'}</span>
                <span>{label}</span>
                {count != null && count > 0 && (
                    <span className="rp-step-count">{count}</span>
                )}
            </button>
            {open && <div className="rp-subbody">{children}</div>}
        </div>
    );
}

const ToolActionRow = ({ action }) => {
    const isActive = action.status === 'started' || action.status === 'start';
    const isFailed = action.status === 'failed';

    return (
        <div className={`rp-tool-action ${isActive ? 'rp-tool-active' : ''} ${isFailed ? 'rp-tool-failed' : ''}`}>
            <span className="rp-tool-icon">
                {isActive ? '⚙' : isFailed ? '×' : '✓'}
            </span>
            <span className="rp-tool-name">{action.tool || 'tool'}</span>
            <span className="rp-tool-status">{action.status}</span>
            {action.args && isActive && (
                <span className="rp-tool-args">
                    {Object.entries(action.args)
                        .slice(0, 2)
                        .map(([k, v]) => `${k}: ${String(v).slice(0, 40)}`)
                        .join(', ')}
                </span>
            )}
            {action.result != null && !isActive && (
                <span className="rp-tool-args">
                    {typeof action.result === 'string'
                        ? action.result.slice(0, 80)
                        : JSON.stringify(action.result).slice(0, 80)}
                </span>
            )}
        </div>
    );
};

const ReasoningStepRow = ({ step, isLast }) => (
    <div className={`rp-step ${isLast ? 'rp-step-latest' : ''}`}>
        <span className="rp-step-dot" />
        <span className="rp-step-text">{step.text}</span>
    </div>
);

const ReasoningPanel = ({
    reasoningSteps = [],
    liveText = '',
    rawTokens = [],
    thinkingText = '',
    toolActions = [],
    isStreaming = false,
    compact = false,
    defaultOpen = false,
    title = 'Details',
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const scrollRef = useRef(null);

    const stepCount = reasoningSteps.length;
    const tokenCount = rawTokens.length;
    const toolCount = toolActions.length;
    const thinking = thinkingText || liveText || '';
    const hasContent = stepCount > 0 || thinking || tokenCount > 0 || toolCount > 0;

    useEffect(() => {
        if (scrollRef.current && isOpen) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [reasoningSteps, liveText, rawTokens, toolActions, isOpen]);

    if (!hasContent && !isStreaming) return null;

    const summaryBits = [];
    if (stepCount) summaryBits.push(`${stepCount} step${stepCount === 1 ? '' : 's'}`);
    if (toolCount) summaryBits.push(`${toolCount} tool${toolCount === 1 ? '' : 's'}`);
    if (tokenCount) summaryBits.push(`${tokenCount} raw token${tokenCount === 1 ? '' : 's'}`);

    return (
        <div className={`reasoning-panel ${isOpen ? 'rp-open' : 'rp-closed'} ${compact ? 'rp-compact' : ''}`}>
            <div
                className="rp-header"
                onClick={() => setIsOpen((prev) => !prev)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setIsOpen((prev) => !prev);
                    }
                }}
            >
                <span className="rp-toggle">{isOpen ? '▾' : '▸'}</span>
                <span className="rp-title">{title}</span>
                {summaryBits.length > 0 && (
                    <span className="rp-step-count">{summaryBits.join(' · ')}</span>
                )}
                {isStreaming && (
                    <span className="rp-streaming-indicator">
                        <span className="rp-spinner" />
                        Thinking…
                    </span>
                )}
            </div>

            {isOpen && (
                <div className="rp-body" ref={scrollRef}>
                    <SubSection label="Steps" count={stepCount} defaultOpen>
                        {reasoningSteps.map((step, i) => (
                            <ReasoningStepRow
                                key={step.seq || i}
                                step={step}
                                isLast={i === stepCount - 1 && !liveText}
                            />
                        ))}
                        {liveText && (
                            <div className="rp-step rp-step-live">
                                <span className="rp-step-dot rp-dot-pulse" />
                                <span className="rp-step-text">{liveText}</span>
                                <span className="rp-cursor" />
                            </div>
                        )}
                    </SubSection>

                    <SubSection
                        label="Thinking"
                        count={thinking ? 1 : 0}
                        defaultOpen={false}
                    >
                        {thinking ? (
                            <pre className="rp-raw-block">{thinking}</pre>
                        ) : (
                            <div className="rp-empty">No thinking stream for this turn.</div>
                        )}
                    </SubSection>

                    <SubSection
                        label="Raw tokens"
                        count={tokenCount}
                        defaultOpen={false}
                    >
                        {tokenCount > 0 ? (
                            <div className="rp-raw-tokens">
                                {rawTokens.map((tok, i) => (
                                    <code key={i} className="rp-token-chip" title={`#${i + 1}`}>
                                        {JSON.stringify(tok)}
                                    </code>
                                ))}
                            </div>
                        ) : (
                            <div className="rp-empty">No raw delta tokens captured.</div>
                        )}
                    </SubSection>

                    <SubSection label="Tools" count={toolCount} defaultOpen={toolCount > 0}>
                        {toolActions.map((action, i) => (
                            <ToolActionRow key={`${action.tool}-${i}`} action={action} />
                        ))}
                    </SubSection>
                </div>
            )}
        </div>
    );
};

export default ReasoningPanel;
